import React from 'react';
import PropTypes from 'prop-types';

import SeatMap, { ZoneSelector } from '@weezevent/throne';
import Availability from '../../../../../models/availability';

import { Trans, useTranslation } from 'react-i18next';

import { useMousePosition } from '../../../../../utils/hooks';

import useAppContext from '../../../../../context/useAppContext';
import { ToastContext } from '@weezevent/nacre';
import useSeatedContext from '../../../../../context/seatedContext';

import { SeatMapBackButton } from '../../../../placement/seatmap-back-button';

import Loading from '../../../../../components/loading';

import '../../../../../styles/attendees/swap-map.css';

function SeatPopover({ seat = null, selectedSeats = {}, selectedAttendees = [] }) {
    const { x, y } = useMousePosition();
    const { t } = useTranslation();

    if (!seat) {
        return null;
    }

    let { left, top } = document.querySelector('.action-modal').getBoundingClientRect();
    let attendee_seat = Object.values(selectedSeats).find(({ newSeat }) => newSeat && newSeat.uuid === seat.uuid);
    if (!attendee_seat) {
        attendee_seat = {};
    }

    let { attendee } = attendee_seat;
    attendee = selectedAttendees.find(att => att.pk === attendee);

    return (
        <div
            style={{
                position: 'absolute',
                top: y - top + 15,
                left: x - left + 15,
                zIndex: 4
            }}
        >
            <div className="attendees-swap-seat-info">
                <div>
                    <Trans
                        i18nKey="seat_info"
                        values={{
                            row: seat.row,
                            seat: seat.seat,
                            access: seat.access
                        }}
                    >
                        Access %access% Row %row% Seat %seat%
                    </Trans>
                </div>
                {attendee ? (
                    <div>
                        <br />
                        {attendee.name}
                    </div>
                ) : null}
                <div>{seat.category_name ? `${t('ticket.chart.category')}: ${seat.category_name}` : ''}</div>
            </div>
        </div>
    );
}

SeatPopover.propTypes = {
    seat: PropTypes.object.isRequired,
    selectedSeats: PropTypes.object.isRequired,
    selectedAttendees: PropTypes.array.isRequired
};

function SeatStyles({ zone = null, selectedAttendees = [], selectedSeats = {}, category, contingent }) {
    let selectedStyles = Object.values(selectedSeats)
        .map(({ newSeat, color }) => {
            if (!newSeat) {
                return null;
            }
            return `
                div.swap-seatmap .seatmap-seat[data-seat-id="${newSeat.uuid}"] svg path {
                    fill: #${color};
                }
            `;
        })
        .join('\n');

    let not_category = category
        ? `
        div.swap-seatmap .seatmap-seat:not([data-category-id="${category}"]) {
            opacity: 0.2;
        }
    `
        : '';

    let not_contingent = contingent
        ? `
        div.swap-seatmap .seatmap-seat:not([data-contingent-id="${contingent}"]) {
            opacity: 0.2;
        }
    `
        : '';

    let bookedStyles = zone.seats
        .filter(seat => seat.active_booking)
        .map(seat => {
            return `
            div.swap-seatmap .seatmap-seat[data-seat-id="${seat.uuid}"] {
                opacity: 0.2;
            }
        `;
        });

    let unavailableStyles = `${not_category}\n${not_contingent}`;

    return (
        <style type="text/css">
            {bookedStyles}
            {unavailableStyles}
            {selectedStyles}
        </style>
    );
}

SeatStyles.propTypes = {
    zone: PropTypes.object.isRequired,
    selectedAttendees: PropTypes.array.isRequired,
    selectedSeats: PropTypes.array.isRequired,
    category: PropTypes.string.isRequired,
    contingent: PropTypes.string.isRequired
};

export default function SwapSeatMap({
    zone = null,
    category = null,
    contingent = null,
    selectedAttendees = [],
    selectedSeats = {},
    onSeatsSelected = () => {},
    onZoneSelected = () => {}
}) {
    const { t } = useTranslation();
    let [{ event, organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const [{ inventory }] = useSeatedContext();

    const [currentZone, setCurrentZone] = React.useState(zone || { uuid: 'root' });
    const [seatOver, setSeatOver] = React.useState(null);
    const [availability, setAvailability] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const setZone = React.useCallback(
        zone => {
            onZoneSelected(zone);
            setCurrentZone(zone);
        },
        [onZoneSelected]
    );

    React.useEffect(() => {
        setLoading(true);
        let params = {
            organizationId: organization.id,
            eventId: event.id,
            inventoryId: inventory.uuid
        };
        if (currentZone.uuid !== 'root') {
            params['zoneId'] = currentZone.uuid;
        }
        Availability.getAll(params, {
            disable_social_distancing: 'true'
        })
            .then(availability => {
                setAvailability(availability);
            })
            .catch(e => {
                toast.error(t('ticket.swap.availability.retrieval_error'));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [currentZone]);

    if (loading) {
        return <Loading />;
    }

    if (!availability?.seats?.length) {
        return (
            <ZoneSelector
                zone={availability}
                onZoneSelected={setZone}
                showAvailability={false}
                unavailableColor={false}
                defaultColor="#0033fa"
                onBackButtonClicked={() => setZone({ uuid: availability.parent })}
            />
        );
    }

    availability.seats.forEach(seat => {
        seat.state = seat.booking_state.toLowerCase();
    });

    return (
        <React.Fragment>
            <SeatStyles zone={availability} category={category} contingent={contingent} selectedAttendees={selectedAttendees} selectedSeats={selectedSeats} />
            <SeatPopover seat={seatOver} selectedAttendees={selectedAttendees} selectedSeats={selectedSeats} />
            <div className="swap-seatmap">
                <SeatMapBackButton name={availability.name || ''} onBack={() => setZone({ uuid: availability.parent })} />
                <SeatMap
                    minimap={false}
                    showScene={false}
                    showPopup={false}
                    zone={availability}
                    onSeatOver={setSeatOver}
                    onSeatClicked={seat => onSeatsSelected([seat])}
                    onSeatsSelected={onSeatsSelected}
                    mapOptions={{
                        showRowNumbers: true,
                        zoomControls: false,
                        offset: false
                    }}
                />
            </div>
        </React.Fragment>
    );
}

SwapSeatMap.propTypes = {
    zone: PropTypes.object.isRequired,
    selectedAttendees: PropTypes.array.isRequired,
    selectedSeats: PropTypes.object.isRequired,
    onSeatsSelected: PropTypes.func,
    onZoneSelected: PropTypes.func,
    category: PropTypes.string.isRequired,
    contingent: PropTypes.string.isRequired
};
